import React, { useState } from "react";
import { Icon } from "../";
import Pagination from "../Data/Pagination";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";

const BusinessesTab = ({
  searchInput,
  setSearchInput,
  handleClearSearch,
  handleSelectAll,
  handleSelectNone,
  handleSelectAllBusinesses, // Optional: if provided, use it; otherwise use internal implementation
  handleBusinessToggle,
  selectedBusinessIds,
  setSelectedBusinessIds, // Required for internal handleSelectAllBusinesses
  isLoadingBusinesses,
  assignBusinessMutation,
  businesses,
  currentPage,
  pagination,
  handlePageChange,
  searchTerm,
  onUpdateBusinesses, // Callback function to update businesses (receives array of business IDs)
  initialSelectedBusinessIds, // For error reversion
}) => {
  const [isLoadingAllBusinesses, setIsLoadingAllBusinesses] = useState(false);

  // Helper to build where clause for business filtering
  const buildBusinessWhereClause = (enabledFilter, searchTerm) => {
    const conditions = [];

    // Add enabled filter condition
    if (enabledFilter === "enabled" || enabledFilter === "disabled") {
      conditions.push({
        attribute: "enabled",
        value: enabledFilter === "enabled",
      });
    }

    // Add search condition for business name
    if (searchTerm && searchTerm.trim() !== "") {
      const _conditions = [
        {
          attribute: "name",
          value: {
            condition: "ilike",
            value: `%${searchTerm.trim()}%`,
          },
        },
      ];
      conditions.push({
        conector: "OR",
        conditions: _conditions,
      });
    }

    return {
      conditions,
      conector: "AND",
    };
  };

  // Internal implementation of handleSelectAllBusinesses
  const handleSelectAllBusinessesInternal = async () => {
    if (!onUpdateBusinesses || !setSelectedBusinessIds) {
      console.warn(
        "handleSelectAllBusinesses: onUpdateBusinesses and setSelectedBusinessIds are required",
      );
      return;
    }

    setIsLoadingAllBusinesses(true);
    const previousSelectedIds = new Set(selectedBusinessIds);

    try {
      // Build where clause with current search term
      const whereClause = buildBusinessWhereClause("all", searchTerm);

      const queryParams = {
        params: "id,name",
        mode: "dashboard",
        where: whereClause,
      };

      const queryString = buildQueryParams(queryParams);
      const response = await apiClient.get(
        `/business${queryString ? `?${queryString}` : ""}`,
      );

      const allBusinesses = response?.result || [];
      const allBusinessIds = new Set(allBusinesses.map((b) => b.id));

      // Merge with currently selected businesses
      const mergedIds = new Set([...selectedBusinessIds, ...allBusinessIds]);
      setSelectedBusinessIds(mergedIds);

      // Call the update callback with all selected business IDs
      const businessArray = Array.from(mergedIds);
      await onUpdateBusinesses(businessArray);
    } catch (error) {
      // Revert on error
      if (initialSelectedBusinessIds) {
        setSelectedBusinessIds(new Set(initialSelectedBusinessIds));
      } else {
        setSelectedBusinessIds(previousSelectedIds);
      }
      console.error("Error selecting all businesses:", error);
    } finally {
      setIsLoadingAllBusinesses(false);
    }
  };

  // Use provided handleSelectAllBusinesses or internal implementation
  const handleSelectAllBusinessesFinal =
    handleSelectAllBusinesses || handleSelectAllBusinessesInternal;

  // Disable select buttons when there are no businesses to select
  const hasNoBusinesses = !isLoadingBusinesses && businesses.length === 0;
  const isSelectDisabled =
    assignBusinessMutation.isPending ||
    isLoadingBusinesses ||
    isLoadingAllBusinesses ||
    hasNoBusinesses;

  return (
    <div className="space-y-6">
      {/* Search and Controls */}
      <div className="rounded-lg border border-gray-700 bg-gray-800 p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Search */}
          <div className="min-w-0 flex-1">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon
                  name="Search"
                  className="h-4 w-4 text-gray-400 sm:h-5 sm:w-5"
                />
              </div>
              <input
                type="text"
                placeholder="Search businesses..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="h-9 w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 pr-8 pl-8 text-sm text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none sm:h-10 sm:px-4 sm:pr-10 sm:pl-10"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute top-1/2 right-2 flex h-4 w-4 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500 sm:h-5 sm:w-5"
                  type="button"
                >
                  <svg
                    className="h-2.5 w-2.5 text-gray-300 hover:text-white sm:h-3 sm:w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Select All/None Buttons */}
          <div className="flex flex-wrap items-center space-x-2">
            <button
              onClick={handleSelectAll}
              disabled={isSelectDisabled}
              className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-blue-400 transition-colors hover:text-blue-300 disabled:cursor-not-allowed disabled:opacity-50"
              title={
                hasNoBusinesses
                  ? "No businesses available to select"
                  : "Select all businesses on this page"
              }
            >
              <Icon name="CheckSquare" className="h-4 w-4" />
              <span>Select All (Page)</span>
            </button>
            {(handleSelectAllBusinesses || onUpdateBusinesses) && (
              <button
                onClick={handleSelectAllBusinessesFinal}
                disabled={isSelectDisabled}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-green-400 transition-colors hover:text-green-300 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  hasNoBusinesses
                    ? "No businesses available to select"
                    : searchTerm
                      ? `Select all businesses matching "${searchTerm}"`
                      : "Select all businesses"
                }
              >
                <Icon name="CheckSquare" className="h-4 w-4" />
                <span>Select All Businesses</span>
              </button>
            )}
            {handleSelectNone && (
              <button
                onClick={handleSelectNone}
                disabled={isSelectDisabled}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-400 transition-colors hover:text-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  hasNoBusinesses
                    ? "No businesses available"
                    : "Deselect all businesses"
                }
              >
                <Icon name="Square" className="h-4 w-4" />
                <span>Select None</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Business List */}
      {isLoadingBusinesses ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500"></div>
            <p className="text-gray-400">Loading businesses...</p>
          </div>
        </div>
      ) : businesses.length > 0 ? (
        <>
          <div className="space-y-3">
            {businesses.map((business) => (
              <div
                key={business.id}
                className="flex items-center justify-between rounded-lg border border-gray-700 p-4 transition-colors hover:bg-gray-800"
              >
                <div className="flex flex-1 items-center space-x-3">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selectedBusinessIds.has(business.id)}
                      onChange={() => handleBusinessToggle(business.id)}
                      disabled={assignBusinessMutation.isPending}
                      className="h-5 w-5 cursor-pointer rounded border-gray-600 bg-gray-800 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>

                  {business.logo ? (
                    <img
                      src={business.logo}
                      alt={business.name}
                      className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gray-700">
                      <Icon name="Store" className="h-6 w-6 text-gray-400" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm leading-tight font-medium text-white">
                      {business.name}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="mt-6">
              <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="text-sm text-gray-400">
                    Showing{" "}
                    <span className="font-medium text-white">
                      {(currentPage - 1) * 10 + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium text-white">
                      {Math.min(currentPage * 10, pagination.total || 0)}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium text-white">
                      {pagination.total || 0}
                    </span>{" "}
                    businesses
                  </div>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.total_pages || 1}
                    onPageChange={handlePageChange}
                    maxVisiblePages={5}
                    disabled={isLoadingBusinesses}
                    className="mt-4 sm:mt-0"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon name="Store" className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-white">
            No Businesses Found
          </h3>
          <p className="text-gray-400">
            {searchTerm
              ? "Try adjusting your search terms."
              : "No businesses available."}
          </p>
        </div>
      )}
    </div>
  );
};

export default BusinessesTab;
